import { useState, useEffect } from 'react';
import useMeasure from 'react-use-measure';

/**
 * The `useScrollPercentage` hook is designed to calculate and track the percentage of an HTML element that has been scrolled through in the viewport.
 * It returns a ref callback to attach to the target element and a state value representing the scroll percentage.
 * This hook is useful for triggering actions based on how much of an element has been scrolled,
 * such as loading additional content or animations as the user scrolls down a page.
 * */
const useScrollPercentage = (): [
  (element: HTMLElement | SVGElement | null) => void,
  number,
] => {
  const [ref, bounds] = useMeasure();
  const [scrollPercentage, setScrollPercentage] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      // Calculate the visible part of the target element
      const windowHeight = window.innerHeight;
      const elementTop = bounds.top;
      const elementHeight = bounds.height;

      // How much of the element has gone up past the top of the screen
      const scrollPosition = window.scrollY;

      // Percentage of element scrolled
      const visibleHeight = Math.min(elementHeight, windowHeight);
      const totalScrollable = elementHeight + windowHeight - visibleHeight;
      const scrolled = Math.max(scrollPosition - elementTop, 0);
      const scrolledPercentage = (scrolled / totalScrollable) * 100;

      setScrollPercentage(Math.min(Math.max(scrolledPercentage, 0), 100));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [bounds]);

  return [ref, scrollPercentage];
};

export default useScrollPercentage;
